/*
  Perfect Noggin cart drawer — plumbing only.
  Loaded with `defer` from layout/theme.liquid. Design and markup live in
  snippets/pn-cart-drawer.liquid; this file only relies on the data-pn-* hooks
  documented at the top of that snippet, so the snippet can be restyled freely.

  Principles (see the handoff):
  - Never construct a hire add-to-cart. Product Rentals Pro does that; we listen
    for its `prp::cart:added` event, cancel its redirect to /cart and open instead.
  - Re-render, never reconstruct. Every change swaps server-rendered markup from
    the `pn-cart-drawer-section` section (Section Rendering API), so whatever
    Rentals Pro / DownPay / Insureful put on the cart survives.
  - Line edits go through /cart/change.js keyed by line_item.key.
  - Removing a hire also removes the app lines that depend on it (the Security
    Hold deposit / insurance sharing its _prp_id) in one /cart/update.js call, so a
    parent can never be left with half a hire. This mirrors Rentals Pro's own
    orphan-cleanup rules.
  - Requests are queued, never parallel. Rapid stepper clicks are debounced.
*/
(function () {
  'use strict';

  var SECTION_ID = 'pn-cart-drawer-section';
  var SOURCE = 'pn-cart-drawer';
  var STEP_DEBOUNCE_MS = 350;
  var OBSERVER_DEBOUNCE_MS = 400;

  var themeRoutes = (window.Theme && window.Theme.routes) || {};
  var cartUrl = themeRoutes.cart_url || '/cart';
  var URLS = {
    cart: cartUrl + '.js',
    add: cartUrl + '/add.js',
    change: cartUrl + '/change.js',
    update: cartUrl + '/update.js',
    sections: '/?sections=' + SECTION_ID
  };

  var state = {
    trigger: null,
    prevBodyOverflow: null,
    busy: 0,
    chain: Promise.resolve(),
    pendingSteps: {},
    lastCount: null,
    stale: false,
    observerTimer: null,
    keydownBound: false
  };

  /* ------------------------------------------------------------------ DOM */

  function root() {
    return document.getElementById('PNCartDrawer');
  }

  function panel() {
    var r = root();
    if (!r) return null;
    return r.querySelector('[data-pn-panel]') || r.querySelector('[role="dialog"]') || r;
  }

  function isOpen() {
    var r = root();
    return !!r && !r.hidden;
  }

  function liveRegion() {
    var p = panel();
    if (!p) return null;
    var el = p.querySelector('.pn-live[data-pn-live]');
    if (!el) {
      el = document.createElement('div');
      el.className = 'pn-live';
      el.setAttribute('data-pn-live', '');
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      p.appendChild(el);
    }
    return el;
  }

  function announce(message) {
    var el = liveRegion();
    if (!el) return;
    el.textContent = '';
    // Force screen readers to re-announce identical messages.
    window.setTimeout(function () {
      el.textContent = message || '';
    }, 30);
  }

  function isVisible(el) {
    if (!el) return false;
    if (el.hidden || el.getAttribute('aria-hidden') === 'true') return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 || rect.height > 0;
  }

  function focusables() {
    var p = panel();
    if (!p) return [];
    var nodes = p.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(nodes, isVisible);
  }

  function focusFirst() {
    var p = panel();
    if (!p) return;
    var close = p.querySelector('[data-pn-close]:not(.pn-scrim):not([data-pn-scrim])');
    var target = (isVisible(close) && close) || focusables()[0] || p;
    if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
  }

  /* ----------------------------------------------------------- busy state */

  function setBusy(delta) {
    state.busy = Math.max(0, state.busy + delta);
    var r = root();
    if (!r) return;
    if (state.busy > 0) r.setAttribute('aria-busy', 'true');
    else r.removeAttribute('aria-busy');
  }

  /* ------------------------------------------------------------ open/close */

  function open(trigger) {
    var r = root();
    if (!r) return;
    if (r.hidden) {
      state.trigger = trigger && trigger.nodeType === 1 ? trigger : document.activeElement;
      state.prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      r.hidden = false;
      r.removeAttribute('hidden');
      if (!state.keydownBound) {
        document.addEventListener('keydown', onKeydown, true);
        state.keydownBound = true;
      }
      focusFirst();
      r.dispatchEvent(new CustomEvent('pn-cart-drawer:open', { bubbles: true }));
    }
    // Always re-sync with the server on open: the page markup may be stale.
    refresh();
  }

  function close() {
    var r = root();
    if (!r || r.hidden) return;
    r.hidden = true;
    r.setAttribute('hidden', '');
    document.body.style.overflow = state.prevBodyOverflow || '';
    state.prevBodyOverflow = null;
    if (state.keydownBound) {
      document.removeEventListener('keydown', onKeydown, true);
      state.keydownBound = false;
    }
    var t = state.trigger;
    state.trigger = null;
    if (t && t.isConnected && typeof t.focus === 'function' && isVisible(t)) {
      t.focus({ preventScroll: true });
    }
    r.dispatchEvent(new CustomEvent('pn-cart-drawer:close', { bubbles: true }));
  }

  function onKeydown(event) {
    if (!isOpen()) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    var items = focusables();
    var p = panel();
    if (!items.length) {
      event.preventDefault();
      if (p) p.focus({ preventScroll: true });
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    var inside = p && p.contains(active);
    if (event.shiftKey) {
      if (!inside || active === first || active === p) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      }
    } else if (!inside || active === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  // If focus escapes the panel while open (e.g. a focused control was re-rendered away), pull it back.
  document.addEventListener('focusin', function (event) {
    if (!isOpen()) return;
    var p = panel();
    if (!p || p.contains(event.target)) return;
    focusFirst();
  });

  /* ------------------------------------------------------------- rendering */

  function describeFocus() {
    var p = panel();
    var active = document.activeElement;
    if (!p || !active || !p.contains(active)) return null;
    var line = active.closest('[data-pn-line]');
    var hooks = ['data-pn-qty-up', 'data-pn-qty-down', 'data-pn-remove', 'data-pn-add', 'data-pn-close', 'data-pn-checkout'];
    var hook = null;
    for (var i = 0; i < hooks.length; i++) {
      if (active.hasAttribute(hooks[i])) {
        hook = hooks[i];
        break;
      }
    }
    return { line: line ? line.getAttribute('data-pn-line') : null, hook: hook };
  }

  function restoreFocus(desc) {
    if (!isOpen()) return;
    var p = panel();
    if (!p) return;
    var target = null;
    if (desc) {
      if (desc.line && desc.hook) {
        var line = p.querySelector('[data-pn-line="' + cssEscape(desc.line) + '"]');
        if (line) target = line.querySelector('[' + desc.hook + ']:not([disabled])');
      } else if (desc.hook) {
        target = p.querySelector('[' + desc.hook + ']:not([disabled])');
      }
    }
    if (!isVisible(target) && !p.contains(document.activeElement)) target = null;
    if (target) {
      target.focus({ preventScroll: true });
    } else if (!p.contains(document.activeElement) || document.activeElement === document.body) {
      focusFirst();
    }
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  }

  // The snippet's count span may sit inside #PNCartDrawer or beside it inside the section
  // wrapper, so count lookups use the wrapper (which contains the drawer) as their scope.
  function countScope() {
    var r = root();
    if (!r) return null;
    return r.closest('.shopify-section') || r.parentElement || r;
  }

  function readCount(scope) {
    var el = scope && scope.querySelector('[data-pn-count]');
    if (!el) return null;
    var n = parseInt((el.textContent || '').replace(/[^\d]/g, ''), 10);
    return isNaN(n) ? null : n;
  }

  // Swap the server-rendered parts into the live drawer. Returns true when the markup was usable.
  function applySection(html) {
    var r = root();
    if (!r || typeof html !== 'string') return false;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var next = doc.getElementById('PNCartDrawer');
    if (!next) return false;

    var focusDesc = describeFocus();
    var selectors = ['[data-pn-headline]', '[data-pn-body]', '.pn-foot'];
    selectors.forEach(function (selector) {
      var current = r.querySelector(selector);
      var incoming = next.querySelector(selector);
      if (current && incoming) current.replaceWith(document.adoptNode(incoming));
    });

    // The shipping strip (.pn-ship) sits between the header and the body, is absent on an
    // empty cart and changes wording with the cart type, so it is inserted/removed as needed.
    var currentShip = r.querySelector('.pn-ship');
    var incomingShip = next.querySelector('.pn-ship');
    if (currentShip && incomingShip) {
      currentShip.replaceWith(document.adoptNode(incomingShip));
    } else if (currentShip) {
      currentShip.remove();
    } else if (incomingShip) {
      var body = r.querySelector('[data-pn-body]');
      if (body && body.parentNode) body.parentNode.insertBefore(document.adoptNode(incomingShip), body);
    }

    // The count span (outside the swapped regions in the approved snippet) is refreshed in place.
    var scope = countScope();
    var currentCount = scope && scope.querySelector('[data-pn-count]');
    var incomingCount = doc.querySelector('[data-pn-count]');
    if (currentCount && incomingCount) currentCount.textContent = incomingCount.textContent;

    // Mirror root-level data-pn-* attributes (cart type etc.) without touching id/class/hidden.
    Array.prototype.forEach.call(next.attributes, function (attr) {
      if (attr.name.indexOf('data-pn-') === 0) r.setAttribute(attr.name, attr.value);
    });

    // Belt and braces: an empty cart must never expose a checkout control.
    var count = readCount(countScope());
    if (count === 0) {
      Array.prototype.forEach.call(r.querySelectorAll('[data-pn-checkout], [name="checkout"], .pn-checkout'), function (el) {
        var form = el.closest('form');
        (form || el).remove();
      });
    }

    state.pendingSteps = {};
    state.stale = false;
    syncCount(count);
    restoreFocus(focusDesc);
    r.dispatchEvent(new CustomEvent('pn-cart-drawer:rendered', { bubbles: true, detail: { itemCount: count } }));
    return true;
  }

  // Mirror the count into any badge outside the drawer, and tell Horizon's cart-icon.
  function syncCount(count) {
    if (count === null || count === undefined) return;
    var scope = countScope();
    Array.prototype.forEach.call(document.querySelectorAll('[data-pn-count]'), function (el) {
      if (scope && scope.contains(el)) return;
      el.textContent = String(count);
      el.toggleAttribute('hidden', count === 0);
    });
    if (state.lastCount === count) return;
    var previous = state.lastCount;
    state.lastCount = count;
    if (previous === null) return; // first paint: the server already rendered the badge
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: {}, sourceId: 'PNCartDrawer', data: { itemCount: count, source: SOURCE } }
      })
    );
  }

  /* ---------------------------------------------------------------- fetch */

  function parseJson(response) {
    return response.text().then(function (text) {
      var json = null;
      try {
        json = text ? JSON.parse(text) : null;
      } catch (e) {
        json = null;
      }
      return { ok: response.ok, status: response.status, json: json };
    });
  }

  function postCart(url, body) {
    var payload = Object.assign({}, body, { sections: SECTION_ID, sections_url: window.location.pathname });
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(parseJson);
  }

  function fetchCart() {
    return fetch(URLS.cart, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(parseJson);
  }

  function fetchSection() {
    return fetch(URLS.sections, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(parseJson)
      .then(function (res) {
        return res.ok && res.json ? res.json[SECTION_ID] : null;
      });
  }

  // Serial queue: cart requests must never overlap or the cart ends up out of order.
  function enqueue(task) {
    setBusy(1);
    var run = function () {
      return Promise.resolve().then(task);
    };
    var p = state.chain.then(run, run);
    state.chain = p.then(
      function () {
        setBusy(-1);
      },
      function () {
        setBusy(-1);
      }
    );
    return p;
  }

  function renderFromServer() {
    return fetchSection().then(function (html) {
      if (!applySection(html)) throw new Error('pn-cart-drawer: section markup unavailable');
    });
  }

  function refresh() {
    return enqueue(renderFromServer).catch(function () {
      state.stale = true;
    });
  }

  // Shared handler for add/change/update responses.
  function handleMutation(res) {
    var json = res.json || {};
    if (!res.ok || json.status) {
      announce(json.description || json.message || 'Sorry, that change did not go through. Please try again.');
      return renderFromServer();
    }
    var html = json.sections && json.sections[SECTION_ID];
    if (!applySection(html)) return renderFromServer();
    return null;
  }

  /* ------------------------------------------------------------- line ops */

  function changeLine(key, quantity) {
    return enqueue(function () {
      return postCart(URLS.change, { id: key, quantity: quantity }).then(handleMutation);
    }).catch(function () {
      announce('Sorry, that change did not go through. Please try again.');
    });
  }

  function isAllowOnceVariant(variantId) {
    var cfg = window.ProductRentalsPro || window.RentalsPro || {};
    var ids = cfg.allow_only_once_variant_ids || [];
    return ids.map(String).indexOf(String(variantId)) !== -1;
  }

  // Removing a hire must also remove the app lines that hang off it (Security Hold deposit,
  // insurance) or the parent is left with half a hire. Rules mirror Rentals Pro's cart-manager.
  function removeHire(key) {
    return enqueue(function () {
      return fetchCart().then(function (res) {
        var cart = res.json || {};
        var items = cart.items || [];
        var updates = {};
        updates[key] = 0;
        var target = null;
        for (var i = 0; i < items.length; i++) {
          if (items[i].key === key) target = items[i];
        }
        var props = (target && target.properties) || {};
        if (props._prp_id) {
          var prefix = String(props._prp_id).split('_')[0];
          var othersWithDeposit = items.some(function (item) {
            return item.key !== key && String((item.properties || {})._has_deposit) === '2';
          });
          items.forEach(function (item) {
            if (item.key === key) return;
            var p = item.properties || {};
            if (!p._prp_deposit && !p._prp_insurance) return;
            var itemPrefix = String(p._prp_id || '').split('_')[0];
            if (p._prp_deposit && isAllowOnceVariant(item.variant_id)) {
              if (!othersWithDeposit) updates[item.key] = 0;
            } else if (itemPrefix === prefix) {
              updates[item.key] = 0;
            }
          });
        }
        return postCart(URLS.update, { updates: updates }).then(handleMutation);
      });
    }).catch(function () {
      announce('Sorry, that change did not go through. Please try again.');
    });
  }

  function removeLine(lineEl) {
    var key = lineEl.getAttribute('data-pn-line');
    if (!key) return;
    // removeHire() inspects the server cart: a line with _prp_id takes its deposit/insurance
    // with it, any other line is a plain single-line removal. No markup hook required.
    return removeHire(key);
  }

  function currentQuantity(lineEl) {
    var key = lineEl.getAttribute('data-pn-line');
    if (state.pendingSteps[key]) return state.pendingSteps[key].quantity;
    var attr = parseInt(lineEl.getAttribute('data-pn-qty') || '', 10);
    if (!isNaN(attr)) return attr;
    var valueEl = lineEl.querySelector('[data-pn-qty-value]');
    if (!valueEl) {
      var down = lineEl.querySelector('[data-pn-qty-down]');
      if (down && down.nextElementSibling && !down.nextElementSibling.hasAttribute('data-pn-qty-up')) {
        valueEl = down.nextElementSibling;
      }
    }
    var parsed = valueEl ? parseInt((valueEl.textContent || '').replace(/[^\d]/g, ''), 10) : NaN;
    return isNaN(parsed) ? 1 : parsed;
  }

  function paintQuantity(lineEl, quantity) {
    lineEl.setAttribute('data-pn-qty', String(quantity));
    var valueEl = lineEl.querySelector('[data-pn-qty-value]');
    if (!valueEl) {
      var down = lineEl.querySelector('[data-pn-qty-down]');
      if (down && down.nextElementSibling && !down.nextElementSibling.hasAttribute('data-pn-qty-up')) {
        valueEl = down.nextElementSibling;
      }
    }
    if (valueEl) valueEl.textContent = String(quantity);
    var downBtn = lineEl.querySelector('[data-pn-qty-down]');
    if (downBtn) downBtn.disabled = quantity <= 1;
  }

  /* ------------------------------------------------------- hire quantity */

  // A hire line's quantity is owned by Product Rentals Pro. The product page widget checks
  // stock for the requested quantity through the app's calendar endpoint
  // (/apps/productrentalspro/calendar?pid=<product>&quantity=<n>) and greys out the dates that
  // cannot supply n units. An increase from the drawer runs the same check for the hire's own
  // date window before anything is committed; decreases never need validation. If the check
  // cannot be completed the increase is refused, never committed unvalidated.

  var MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };

  // Rentals Pro writes dates as "E, dd MMM yyyy" (e.g. "Wed, 30 Sep 2026"); also accept ISO.
  function parseHireDate(value) {
    if (!value) return null;
    var s = String(value).trim();
    var iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    var m = s.replace(/^[A-Za-z]+,\s*/, '').match(/^(\d{1,2})\s+([A-Za-z]{3})[A-Za-z]*\s+(\d{4})$/);
    if (!m || MONTHS[m[2].toLowerCase()] === undefined) return null;
    return new Date(Number(m[3]), MONTHS[m[2].toLowerCase()], Number(m[1]));
  }

  function isoDate(d) {
    var mm = String(d.getMonth() + 1);
    var dd = String(d.getDate());
    return d.getFullYear() + '-' + (mm.length < 2 ? '0' + mm : mm) + '-' + (dd.length < 2 ? '0' + dd : dd);
  }

  function calendarUrl(productId, quantity) {
    return '/apps/productrentalspro' + (window.prpProxySuffix || '') + '/calendar?pid=' + encodeURIComponent(productId) + '&quantity=' + encodeURIComponent(quantity);
  }

  // Resolves to { ok: true } or { ok: false, reason: 'refused' | 'unverified' }.
  function checkHireAvailability(line, quantity) {
    var props = line.properties || {};
    var start = parseHireDate(props['Delivery Date Target']);
    var end = parseHireDate(props['Return Date To Send Back To Us']);
    if (!start || !end || !line.product_id) return Promise.resolve({ ok: false, reason: 'unverified' });
    return fetch(calendarUrl(line.product_id, quantity), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(parseJson)
      .then(function (res) {
        var data = res.ok && res.json;
        if (!data || !Array.isArray(data.variants)) return { ok: false, reason: 'unverified' };
        var variant = null;
        for (var i = 0; i < data.variants.length; i++) {
          if (String(data.variants[i].shopifyVariantId) === String(line.variant_id)) variant = data.variants[i];
        }
        if (!variant || !variant.isRental) return { ok: false, reason: 'unverified' };
        if (variant.available === false) return { ok: false, reason: 'refused' };
        var methods = variant.deliveryMethods || [];
        var method = null;
        for (var j = 0; j < methods.length; j++) {
          if (methods[j].name === props._prp_delivery_method) method = methods[j];
        }
        if (!method) method = methods[0];
        if (!method) return { ok: false, reason: 'unverified' };
        if (method.allDatesUnavailable) return { ok: false, reason: 'refused' };
        var unavailable = {};
        (method.unavailableDates || []).forEach(function (d) {
          unavailable[String(d).slice(0, 10)] = true;
        });
        // Same rule the widget applies to a selected range: no unavailable date inside it.
        for (var d = new Date(start.getTime()); d.getTime() <= end.getTime(); d.setDate(d.getDate() + 1)) {
          if (unavailable[isoDate(d)]) return { ok: false, reason: 'refused' };
        }
        return { ok: true };
      })
      .catch(function () {
        return { ok: false, reason: 'unverified' };
      });
  }

  // Commit a validated hire quantity. Rentals Pro encodes the quantity in the _prp_id suffix
  // ("<timestamp>_<qty>") when it adds the line, so the suffix is kept in step with the new
  // quantity. /cart/change.js replaces the whole properties hash, so every property is re-sent.
  function commitHireQuantity(line, quantity) {
    var props = {};
    Object.keys(line.properties || {}).forEach(function (k) {
      props[k] = line.properties[k];
    });
    if (props._prp_id) props._prp_id = String(props._prp_id).split('_')[0] + '_' + quantity;
    return postCart(URLS.change, { id: line.key, quantity: quantity, properties: props }).then(handleMutation);
  }

  function changeHireQuantity(key, quantity) {
    return enqueue(function () {
      return fetchCart().then(function (res) {
        var items = (res.json && res.json.items) || [];
        var line = null;
        for (var i = 0; i < items.length; i++) {
          if (items[i].key === key) line = items[i];
        }
        if (!line) return renderFromServer();
        if (quantity <= line.quantity) return commitHireQuantity(line, quantity);
        return checkHireAvailability(line, quantity).then(function (verdict) {
          if (verdict.ok) return commitHireQuantity(line, quantity);
          announce(
            verdict.reason === 'refused'
              ? 'Sorry, we do not have ' + quantity + ' available for those dates. Your hire stays at ' + line.quantity + '.'
              : 'Sorry, we could not check availability just now. Your hire stays at ' + line.quantity + '.'
          );
          return renderFromServer();
        });
      });
    }).catch(function () {
      announce('Sorry, that change did not go through. Please try again.');
    });
  }

  // Stepper: paint optimistically, debounce, then send one request with the final quantity.
  function step(lineEl, delta) {
    var key = lineEl.getAttribute('data-pn-line');
    if (!key) return;
    var next = Math.max(1, currentQuantity(lineEl) + delta);
    paintQuantity(lineEl, next);
    var pending = state.pendingSteps[key];
    if (pending) window.clearTimeout(pending.timer);
    state.pendingSteps[key] = {
      quantity: next,
      timer: window.setTimeout(function () {
        delete state.pendingSteps[key];
        // A hire line's + carries data-pn-hire-qty: its increase must be validated with Rentals Pro.
        if (lineEl.querySelector('[data-pn-hire-qty]')) changeHireQuantity(key, next);
        else changeLine(key, next);
      }, STEP_DEBOUNCE_MS)
    };
  }

  function addVariant(variantId, button) {
    if (!variantId) return;
    if (button) button.disabled = true;
    return enqueue(function () {
      return postCart(URLS.add, { items: [{ id: variantId, quantity: 1 }] }).then(handleMutation);
    })
      .catch(function () {
        announce('Sorry, that could not be added. Please try again.');
      })
      .then(function () {
        if (button && button.isConnected) button.disabled = false;
      });
  }

  /* --------------------------------------------------------------- events */

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var opener = target.closest('[data-pn-open]');
    if (opener && !(root() && root().contains(opener))) {
      event.preventDefault();
      open(opener);
      return;
    }

    var r = root();
    if (!r || !r.contains(target)) return;

    var closer = target.closest('[data-pn-close]');
    if (closer) {
      event.preventDefault();
      close();
      return;
    }

    // App-owned lines (data-pn-locked: the Security Hold deposit) are read-only. The snippet
    // renders no controls for them; this guard makes sure nothing can edit or remove one anyway.
    if (target.closest('[data-pn-locked]') && target.closest('[data-pn-qty-up], [data-pn-qty-down], [data-pn-remove]')) {
      event.preventDefault();
      return;
    }

    var up = target.closest('[data-pn-qty-up]');
    if (up) {
      event.preventDefault();
      var upLine = up.closest('[data-pn-line]');
      if (upLine) step(upLine, 1);
      return;
    }

    var down = target.closest('[data-pn-qty-down]');
    if (down) {
      event.preventDefault();
      var downLine = down.closest('[data-pn-line]');
      if (downLine && currentQuantity(downLine) > 1) step(downLine, -1);
      return;
    }

    var remove = target.closest('[data-pn-remove]');
    if (remove) {
      event.preventDefault();
      var removeLineEl = remove.closest('[data-pn-line]');
      if (removeLineEl) removeLine(removeLineEl);
      return;
    }

    var add = target.closest('[data-pn-add]');
    if (add) {
      event.preventDefault();
      addVariant(add.getAttribute('data-pn-add'), add);
    }
  });

  // Never let an empty cart submit to checkout, even if markup slipped through.
  document.addEventListener('submit', function (event) {
    var r = root();
    var form = event.target;
    if (!r || !(form instanceof HTMLFormElement) || !r.contains(form)) return;
    if (readCount(countScope()) === 0) event.preventDefault();
  });

  // Horizon's own cart events (product-form-component for plain variants such as the cover,
  // cart-items-component on the cart page, quick-add). Refresh; open only on a real add.
  document.addEventListener('cart:update', function (event) {
    var detail = (event && event.detail) || {};
    var data = detail.data || {};
    if (data.source === SOURCE) return;
    if (data.didError) return;
    var isAdd = data.source === 'product-form-component';
    var r = root();
    if (!r) return;
    if (isAdd) {
      open(document.activeElement); // open() refreshes
    } else if (isOpen()) {
      refresh();
    } else {
      state.stale = true;
    }
  });

  // Product Rentals Pro owns the hire (and Buy New on the same widget). Let it add, then
  // cancel its redirect to /cart and open the drawer. If we cannot render, fall back to /cart.
  document.addEventListener('prp::cart:added', function (event) {
    var r = root();
    if (!r) return;
    if (typeof event.preventDefault === 'function') event.preventDefault();
    if (event.detail && event.detail.whenPrevented) {
      event.detail.whenPrevented.redirect = false;
      // Keep the widget in its "Added" state. Letting it reset() after a prevented add throws
      // Rentals Pro's error_5 ("Problem with the current variant") in the console.
      event.detail.whenPrevented.dontReset = true;
    }
    var trigger = document.activeElement;
    enqueue(renderFromServer).then(
      function () {
        open(trigger);
      },
      function () {
        window.location.href = cartUrl;
      }
    );
  });

  // Apps mutate the cart behind our back (Rentals Pro removes expired hires or orphaned
  // deposits, Insureful toggles its line, DownPay updates attributes). Watch the network for
  // cart mutations we did not issue and re-render.
  if ('PerformanceObserver' in window) {
    try {
      var observer = new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        var external = false;
        for (var i = 0; i < entries.length; i++) {
          var name = entries[i].name || '';
          var initiator = entries[i].initiatorType;
          if (initiator !== 'fetch' && initiator !== 'xmlhttprequest') continue;
          if (!/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(name)) continue;
          if (state.busy > 0) continue; // our own request is in flight
          external = true;
        }
        if (!external) return;
        window.clearTimeout(state.observerTimer);
        state.observerTimer = window.setTimeout(function () {
          if (isOpen()) refresh();
          else state.stale = true;
        }, OBSERVER_DEBOUNCE_MS);
      });
      observer.observe({ type: 'resource', buffered: false });
    } catch (e) {
      /* PerformanceObserver unsupported for resources: the drawer still refreshes on open */
    }
  }

  // Back/forward cache: the cart may have changed while we were away.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      if (isOpen()) refresh();
      else state.stale = true;
    }
  });

  // Theme editor reloads the section: re-establish first-paint count.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.detail && event.detail.sectionId === SECTION_ID) state.lastCount = readCount(countScope());
  });

  /* ----------------------------------------------------------------- init */

  function init() {
    var r = root();
    if (!r) return;
    if (!r.hasAttribute('hidden')) r.setAttribute('hidden', '');
    state.lastCount = readCount(countScope());
    liveRegion();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.PNCartDrawer = { open: open, close: close, refresh: refresh, isOpen: isOpen };
})();
